// Advanced performance monitoring for HDF Hauling website

interface PerformanceMetric {
  name: string;
  value: number;
  unit: string;
  timestamp: number;
}

interface ImageLoadMetric {
  src: string;
  loadTime: number;
  size: number;
  fromCache: boolean;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private imageMetrics: ImageLoadMetric[] = [];
  private observer: PerformanceObserver | null = null;

  constructor() {
    this.initializeObserver();
    this.monitorResourceLoading();
  }

  private initializeObserver() {
    if ('PerformanceObserver' in window) {
      this.observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.processEntry(entry);
        }
      });

      // Observe different performance entry types
      try {
        this.observer.observe({ entryTypes: ['paint', 'navigation', 'resource', 'largest-contentful-paint'] });
      } catch (e) {
        // Fallback for older browsers
        this.observer.observe({ entryTypes: ['paint', 'navigation', 'resource'] });
      }
    }
  }

  private processEntry(entry: PerformanceEntry) {
    const metric: PerformanceMetric = {
      name: entry.name || entry.entryType,
      value: entry.startTime,
      unit: 'ms',
      timestamp: Date.now(),
    };

    // Special handling for different entry types
    if (entry.entryType === 'paint') {
      metric.name = `paint-${entry.name}`;
      metric.value = entry.startTime;
    } else if (entry.entryType === 'largest-contentful-paint') {
      metric.name = 'largest-contentful-paint';
      metric.value = entry.startTime;
    } else if (entry.entryType === 'resource') {
      const resourceEntry = entry as PerformanceResourceTiming;
      if (this.isImageResource(resourceEntry.name)) {
        this.trackImageLoad({
          src: resourceEntry.name,
          loadTime: resourceEntry.responseEnd - resourceEntry.startTime,
          size: resourceEntry.transferSize || 0,
          fromCache: resourceEntry.transferSize === 0,
        });
      }
    }

    this.metrics.push(metric);
    this.reportMetric(metric);
  }

  private isImageResource(url: string): boolean {
    return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url);
  }

  private trackImageLoad(imageMetric: ImageLoadMetric) {
    this.imageMetrics.push(imageMetric);
    
    // Log slow image loads in development
    if (process.env.NODE_ENV === 'development' && imageMetric.loadTime > 1000) {
      console.warn(`Slow image load detected:`, {
        src: imageMetric.src.split('/').pop(),
        loadTime: `${imageMetric.loadTime.toFixed(2)}ms`,
        size: `${(imageMetric.size / 1024).toFixed(2)}KB`,
        fromCache: imageMetric.fromCache,
      });
    }
  }

  private monitorResourceLoading() {
    // Monitor initial page load metrics
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.collectInitialMetrics();
      }, 100);
    });
  }

  private collectInitialMetrics() {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    if (navigation) {
      const metrics = [
        { name: 'dns-lookup', value: navigation.domainLookupEnd - navigation.domainLookupStart },
        { name: 'tcp-connection', value: navigation.connectEnd - navigation.connectStart },
        { name: 'request-response', value: navigation.responseEnd - navigation.requestStart },
        { name: 'dom-parsing', value: navigation.domContentLoadedEventEnd - navigation.responseEnd },
        { name: 'resource-loading', value: navigation.loadEventStart - navigation.domContentLoadedEventEnd },
        { name: 'total-load-time', value: navigation.loadEventEnd - navigation.startTime },
      ];

      metrics.forEach(metric => {
        if (metric.value > 0) {
          this.reportMetric({
            ...metric,
            unit: 'ms',
            timestamp: Date.now(),
          });
        }
      });
    }
  }

  private reportMetric(metric: PerformanceMetric) {
    if (process.env.NODE_ENV === 'development') {
      // Log performance metrics in development
      if (metric.name.includes('paint') || metric.name.includes('contentful') || metric.value > 1000) {
        console.log(`📊 Performance: ${metric.name} = ${metric.value.toFixed(2)}${metric.unit}`);
      }
    }

    // In production, you would send to analytics
    // this.sendToAnalytics(metric);
  }

  // Get performance summary
  public getPerformanceSummary() {
    const imageStats = this.getImageLoadStats();
    const coreMetrics = this.getCoreWebVitals();
    
    return {
      totalMetrics: this.metrics.length,
      imageStats,
      coreMetrics,
      slowResources: this.getSlowResources(),
    };
  }

  private getImageLoadStats() {
    if (this.imageMetrics.length === 0) return null;

    const totalImages = this.imageMetrics.length;
    const totalSize = this.imageMetrics.reduce((sum, img) => sum + img.size, 0);
    const avgLoadTime = this.imageMetrics.reduce((sum, img) => sum + img.loadTime, 0) / totalImages;
    const cachedImages = this.imageMetrics.filter(img => img.fromCache).length;

    return {
      totalImages,
      totalSize: Math.round(totalSize / 1024), // KB
      avgLoadTime: Math.round(avgLoadTime),
      cacheHitRate: Math.round((cachedImages / totalImages) * 100),
      slowImages: this.imageMetrics.filter(img => img.loadTime > 1000).length,
    };
  }

  private getCoreWebVitals() {
    const lcp = this.metrics.find(m => m.name === 'largest-contentful-paint');
    const fcp = this.metrics.find(m => m.name === 'paint-first-contentful-paint');
    const fmp = this.metrics.find(m => m.name === 'paint-first-meaningful-paint');

    return {
      largestContentfulPaint: lcp ? Math.round(lcp.value) : null,
      firstContentfulPaint: fcp ? Math.round(fcp.value) : null,
      firstMeaningfulPaint: fmp ? Math.round(fmp.value) : null,
    };
  }

  private getSlowResources() {
    return this.imageMetrics
      .filter(img => img.loadTime > 1000)
      .map(img => ({
        name: img.src.split('/').pop() || 'unknown',
        loadTime: Math.round(img.loadTime),
        size: Math.round(img.size / 1024),
      }))
      .sort((a, b) => b.loadTime - a.loadTime);
  }

  public disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

// Helper function to track custom performance markers
export const markPerformance = (name: string) => {
  if ('performance' in window && 'mark' in performance) {
    performance.mark(name);
  }
};

// Helper function to measure performance between markers
export const measurePerformance = (name: string, startMark: string, endMark?: string) => {
  if ('performance' in window && 'measure' in performance) {
    try {
      performance.measure(name, startMark, endMark);
    } catch (e) {
      console.warn('Performance measurement failed:', e);
    }
  }
};

// Export performance summary for debugging
export const logPerformanceSummary = () => {
  if (process.env.NODE_ENV === 'development') {
    console.group('🚀 HDF Hauling Performance Summary');
    console.table(performanceMonitor.getPerformanceSummary());
    console.groupEnd();
  }
};